<template>
  <div class='wrapper-page'>
    <modal ref="deleteMessageForm">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('communication.delete_messages')}}
        </h5>
      </template>
      <template #body>
        <form>
          <div class='modal-body'>
            <p>{{ $t('communication.are_you_sure_you_wanna_delete')}}</p>
          </div>
          <div class='modal-footer'>
            <input type='reset' class='btn btn-outline-danger float-right' :value="$t('communication.delete_messages_button')" @click.prevent='deleteSentMessage' />
          </div>
        </form>
      </template>
    </modal>
    <modal ref='filtersModal' class="filter-all-messages">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('shared.filter') }}
        </h5>
      </template>

      <template #body>
        <form @submit.prevent='filterSentEmails'>
          <div class='modal-body'>
            <div class='form-group form-inline'>
              <label>{{ $t('folders_list.starred_folders')}}</label>
              <label>
                <input type='radio' v-model='starred' value='true'>
                {{ $t('folders_list.yes_way') }}
              </label>

              <label>
                <input type='radio' v-model='starred' value='false'>
                {{ $t('folders_list.no_way') }}
              </label>
              <label>
                <i class='fas fa-times' :title="$t('records_list.remove_selection')" @click.prevent='starred = null'></i>
              </label>
            </div>
            <div class="form-group">
              <label>{{ $t('communication.to') }}</label>
              <infinite-multi-select :getter="getContacts" v-model="toContactFilters" />
            </div>
            <div class="form-group">
              <label>
                {{ $t('communication.status') }}
              </label>
              <multi-select :options="messageStatus" v-model="messageStatusFilters" />
            </div>
            <div class="form-group">
              <label>{{ $t('communication.keywords') }}</label>
              <label>
                <input type="text" name="search" v-model="searchInSent" class="form-control">
              </label>
            </div>
            <div class="form-group">
              <label>{{ $t('communication.tags') }}</label>
              <infinite-multi-select :getter="getTags" v-model="tagsFilters" />
            </div>
            <div class="form-group">
              <label>{{ $t('communication.sent_date') }}</label>
              <div class="date">
                <label>{{ $t('communication.from') }}</label>
                <datepicker
                  ref="datepicker"
                  name="document_request[sending_date]"
                  v-model="fromSendingDate"
                  input-class="form-control expiration-input"
                  :inline="false"
                  :typeable="true"
                  :full-month-name="true"
                  :monday-first="true"
                  :placeholder="$t('expiration.pick_date')">
                </datepicker>
              </div>
              <div class="date">
                <label>{{ $t('communication.to') }}</label>
                <datepicker
                  ref="datepicker"
                  name="document_request[sending_date]"
                  v-model="toSendingDate"
                  input-class="form-control expiration-input"
                  :inline="false"
                  :typeable="true"
                  :full-month-name="true"
                  :monday-first="true"
                  @selected="this.$refs.datepicker.close()"
                  :placeholder="$t('expiration.pick_date')">
                </datepicker>
              </div>
            </div>
          </div>

          <div class='modal-footer'>
            <input type='reset' class='btn btn-neutral float-right' :value="$t('folders_list.clear_filters')" @click.prevent='resetFilters' />
            <input type='submit' class='btn btn-outline-primary float-right' :value="$t('folders_list.filter')" />
          </div>
        </form>
      </template>
    </modal>
    <div class='container-fluid text-center'>
      <h1 class='page-title'>
        {{ $t('communication.automated_emails') }} ({{meta.total}})
        <a :href="automatedVideo()" target="_blank" class="own-tooltip own-tooltip-bottom video-link" :data-tooltip="$t('communication.click_here_to_watch_automated')"><img src="../../assets/video.svg" /></a>
      </h1>
      <div class="actions-on-table">
        <div class='filter'>
          <a  @click.prevent="openModal($refs.filtersModal)" href="#" class="filter-by btn btn-neutral">
            {{ $t('root.filter_all_data_by') }}
          </a>
        </div>
      </div>
      <div class="filter-results" v-if="filterMessages">
        <strong v-if="hasFilters">{{ $t('root.filter_results_for') }}</strong>
        <div v-if="starred !== null">
          <p>{{ $t('root.filters.starred') }}
            <strong v-if="starred === 'true'">{{ $t('root.filters.yes_way') }}</strong>
            <strong v-if="starred === 'false'">{{ $t('root.filters.no_way') }}</strong>
          </p>
        </div>
        <div v-if="toContactFilters.length > 0">
          <p>{{ $t('communication.filters.to') }} <strong v-for="contact in toContactFilters">{{contact.name}}</strong></p>
        </div>
        <div v-if="messageStatusFilters.length > 0">
          <p>{{ $t('communication.filters.status') }} <strong v-for="status in messageStatusFilters">{{status.name}}</strong></p>
        </div>
        <div v-if="searchInSent !== ''">
          <p>{{ $t('communication.filters.keywords') }} <strong>{{searchInSent}}</strong></p>
        </div>
        <div v-if="tagsFilters.length > 0">
          <p>{{ $t('communication.filters.tags') }} <strong v-for="tag in tagsFilters">{{tag.name}}</strong></p>
        </div>
        <div v-if="fromSendingDate !== null">
          <p>{{ $t('communication.filters.receiving_date') }} <strong>{{formatDate(fromSendingDate)}}</strong></p>
        </div>
        <div v-if="toSendingDate !== null">
          <p>{{ $t('communication.filters.until_date') }} <strong>{{formatDate(toSendingDate)}}</strong></p>
        </div>

        <a v-if="hasFilters" @click.prevent="resetFilters" href="#">{{ $t('records_list.clear_filters') }}</a>
      </div>
      <loading v-if="loading" />
      <div v-else-if='automatedEmails.length === 0 && !loading' class="empty-list-wrapper">
        <img src="../../assets/empty_states/envelope.svg" />
        <p>{{$t('communication.automated_empty_state')}}</p>
      </div>
      <div class="table_wrapper" v-else-if='automatedEmails.length > 0 && !loading'>
        <div :class="['bulk-container', { 'hide': bulkOpMessages.length === 0  }]">
          <span>{{ bulkOpMessages.length }} {{ $t('root.automated_messages') }}</span>
          <ul>
            <li>
              <a href="#" class="dropdown-item" @click.prevent="$refs.bulkTagMessages.open(null, null, null, bulkOpMessages, null)">
                <i class="fas fa-tag"></i>
                {{ $t('communication.tags')}}
              </a>
              <bulk-tag-messages
                ref="bulkTagMessages"
                @success="addTagsToBulkMessages()">
              </bulk-tag-messages>
            </li>
            <li>
              <a @click.prevent='openDeleteMessageModal(bulkOpMessages)' href="#" rel="nofollow" class="dropdown-item">
                <i class="far fa-trash-alt"></i>
                {{ $t('communication.delete')}}
              </a>
            </li>
          </ul>
        </div>
        <table class="table">
          <thead>
          <tr>
            <th><input type='checkbox' @input='selectAll' /></th>
            <th></th>
            <th>{{ $t('communication.to')}}</th>
            <th>{{ $t('communication.subject')}}</th>
            <th>{{ $t('communication.tags')}}</th>
            <th>{{ $t('communication.sent_on')}}</th>
            <th>{{ $t('communication.status')}}</th>
            <th>{{ $t('communication.actions')}}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="message in automatedEmails">
            <td class="bulk-check">
              <input type='checkbox' :value='message.id' v-model='bulkOpMessages' />
            </td>
            <td>
              <a href='#' class="starred" @click.prevent="starMessage(message)">
                <i :class="['fa-star', {'far': !message.starred, 'fas': message.starred }]"></i>
              </a>
            </td>
            <td>
              <router-link :to="{name: 'message', params: { id: message.id, referrer: $route.name, messageType: $route.name }}">
                {{ message.contact.name }} - {{ message.contact.email }}
              </router-link>
            </td>
            <td>
              <span v-if="message.subject">{{ message.subject }}</span>
              <span v-else>{{ $t('communication.no_subject')}}</span>
            </td>
            <td>
              <tag-manager
                resourceType="automatedEmail"
                :resource='message'
                @reload-list="getAutomatedEmails()">
              </tag-manager>
              <div class="tag-dropdown-column">
                <div class="existing-tags-wrapper" :class="tag.color"
                     v-for="tag in message.tags">
                  <div class="btn-group btn-group-toggle" data-toggle="buttons">
                    <label class="btn active">
                      <router-link :to="{name: 'tag', params: { id: tag.id }}">
                        {{tag.name}}
                      </router-link>
                    </label>
                    <label class="btn" @click.prevent="removeConnectedTag(tag, message.id)">
                      <i class="fa fa-remove"></i>
                    </label>
                  </div>
                </div>
              </div>
            </td>
            <td>{{ formatDate(message.updated_at) }}</td>
            <td>
              <i :class="`fas fa-circle ${ statusClass(message.status) }`" />
              {{ statusText(message.status) }}
            </td>
            <td class="dropdown-column">
              <div class="dropdown">
                <button aria-expanded="false" aria-haspopup="true" data-toggle="dropdown" type="button" class="btn dropdown-toggle">· · ·</button>
                <div class="dropdown-menu dropdown-menu-right">
                  <ul>
                    <li>
                      <a @click.prevent='openDeleteMessageModal([message.id])' href="#" rel="nofollow" class="dropdown-item">
                        <i class="far fa-trash-alt"></i>
                        {{ $t('communication.delete')}}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <div class="empty-list-wrapper" v-else-if='automatedEmails.length === 0 && !hasFilters'>
        <p>{{ $t('root.no_data') }}</p>
      </div>

      <div class="empty-list-wrapper" v-else-if='automatedEmails.length === 0 && hasFilters'>
        <p>{{ $t('root.no_data_filters') }}</p>
        <a href="#" @click.prevent="$refs.filtersModal.reset">{{ $t('root.clear_filters') }}</a>
      </div>
      <pagination :meta='meta'/>
    </div>
  </div>
</template>

<script>
import _modal from '../../components/_modal'
import _pagination from "../../components/_pagination"
import _loading from '../../components/_loading'
import tag_manager from "../../components/tags/_tags_manager"
import bulk_tag_messages from "../../components/tags/_bulk_tag_messages"
import _multi_select from "../../components/shared/_multi_select"
import _infinite_multi_select from "../../components/shared/_infinite_multi_select"
import Datepicker from 'vuejs-datepicker'
export default {
  name: 'AutomatedEmails',
  components: {
    'multi-select': _multi_select,
    'infinite-multi-select': _infinite_multi_select,
    'modal': _modal,
    pagination: _pagination,
    'loading': _loading,
    'tag-manager': tag_manager,
    'datepicker': Datepicker,
    'bulk-tag-messages': bulk_tag_messages
  },
  data() {
    return {
      loading: true,
      bulkOpMessages: [],
      messageIds: [],
      toContactFilters: [],
      tagsFilters: [],
      messageStatusFilters: [],
      fromSendingDate: null,
      toSendingDate: null,
      searchInSent: '',
      starred: null,
    }
  },
  watch: {
    query() {
      this.getAutomatedEmails()
    }
  },
  created() {
    this.getAutomatedEmails()
    this.store.sendMetrics({name: 'automated_emails', params: this.$route.params, path: this.$route.fullPath})
  },
  computed: {
    automatedEmails() {
      return this.state.automatedEmails || []
    },
    contacts() {
      return this.lists.contacts || []
    },

    tags() {
      return this.lists.tags || []
    },
    messageStatus() {
      return [
        { id: 'pending', name: (this.$t('communication.status_message.pending')) },
        { id: 'failed', name: (this.$t('communication.status_message.failed')) },
        { id: 'delivered', name: (this.$t('communication.status_message.delivered')) },
        { id: 'opened', name: (this.$t('communication.status_message.opened')) },
      ]
    }

  },
  methods: {
    getAutomatedEmails() {
      this.store.getAutomatedEmails(this.query).then(() =>  this.loading = false)
    },
    openModal(modal) {
      modal.open()
    },
    starMessage(message) {
      return this.store.updateAutomatedEmails(message.id, { starred: !message.starred}).then(() => {
        this.store.sendMetrics({name: 'star_automated_email', params: this.$route.params, path: this.$route.fullPath})
        this.getAutomatedEmails()
      })
    },
    removeConnectedTag(tag, automatedEmailId) {
      this.store.sendMetrics({name: 'remove_tag_from_automated_email', params: this.$route.params, path: this.$route.fullPath})
      this.store.removeConnectedTag(tag.id, { sent_email_id: automatedEmailId }).then(() => {
        this.getAutomatedEmails()
      })
    },
    filterMessages() {
      return this.toContactFilters.length > 0 ||
        this.tagsFilters.length > 0 || this.messageStatusFilters.length > 0 || this.fromSendingDate !== null ||
        this.toSendingDate !== null || this.searchInSent !== '' || this.starred !== null
    },
    getContacts(params) {
      return this.listStore.getContacts(params)
    },
    getTags(params) {
      return this.listStore.getTags(params)
    },
    selectAll(e) {
      this.bulkOpMessages = e.target.checked ? this.automatedEmails.map(i => i.id) : []
    },
    closeDatePicker() {
      this.$refs.datepicker.close()
    },
    filtersOn() {
      this.$refs.filtersModal.close()
    },
    resetFilters() {
      this.toContactFilters= []
      this.tagsFilters = []
      this.messageStatusFilters = []
      this.fromSendingDate = null
      this.toSendingDate = null
      this.searchInSent = ''
      this.starred = null
      this.$router.push({ query: null})
      this.$refs.filtersModal.close()
    },
    filterSentEmails() {
      let q  = {
        'filters[to]': this.toContactFilters.length > 0 ? this.toContactFilters.map(c => c.id) : null,
        'filters[status]': this.messageStatusFilters.length > 0 ? this.messageStatusFilters.map(c => c.id) : null,
        'filters[tags]': this.tagsFilters.length > 0 ? this.tagsFilters.map(c => c.id) : null,
        'filters[from_sending_date]': this.fromSendingDate !== null ? this.formatDate(this.fromSendingDate) : null,
        'filters[to_sending_date]': this.toSendingDate !== null ? this.formatDate(this.toSendingDate) : null,
        'filters[search_query]': this.searchInSent.length > 2 ? this.searchInSent : null,
        'filters[starred]': this.starred !== null ? this.starred : null,
        'page': this.query.page || 1
      }
      const f = Object.entries(q).reduce((a,[k,v]) => (v === null ? a : (a[k]= v, a)), {})

      this.$refs.filtersModal.close()
      this.$router.push({ query: f })
    },
    openDeleteMessageModal(messageIds) {
      this.messageIds = messageIds
      this.$refs.deleteMessageForm.open()
    },
    deleteSentMessage() {
      this.store.sendMetrics({name: 'delete_automated_email', params: this.$route.params, path: this.$route.fullPath})
      this.$refs.deleteMessageForm.close()
      this.store.deleteAutomatedEmail(this.messageIds).then(() => {
        this.bulkOpMessages = []
        this.messageIds= []
        this.getAutomatedEmails()
      })
    },
    addTagsToBulkMessages() {
      this.store.sendMetrics({name: 'bulk_add_tag_to_automated_email', params: this.$route.params, path: this.$route.fullPath})
      this.bulkOpMessages = []
      this.getAutomatedEmails()
    },
    automatedVideo(){
      if(this.locale === 'de')
        return 'https://relatico.wistia.com/medias/v3tfyup3os'
      return 'https://relatico.wistia.com/medias/que7k2d7xe'
    }
  },

}
</script>

<style lang="sass" scoped>
.actions-on-table
  float: left
  width: 100%
.filter-all-messages
  form
    text-align: left
    label
      text-align: left
      display: block
    .form-group
      margin-bottom: 20px
      .date
        width: 47%
        display: inline-block
        margin-right: 10px
    .custom-select
      width: 100%
    .form-inline
      label
        width: 20%
        &:first-child
          width: 50%
        &:last-child
          width: 5%
          color: #686868
          cursor: pointer
.bulk-container
  background-color: #EBF4FE
  display: flex
  align-items: center
  padding: 8px 0
  border-radius: 3px
  float: left
  width: 100%
  > span
    padding-left: 10px
  ul
    list-style: none
    padding: 0
    margin: 0
    display: flex
    li
      position: relative
      a
        &:hover, &:visited, &:focus
          background: none
          text-decoration: underline
  &.hide
    display: none
</style>
